import styled from 'styled-components';
import { SummaryDealMip } from '@dealstack/shared/lib/generated';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Badge, Divider, Flex, MantineColor, Text, BadgeProps } from '@mantine/core';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { SummaryItem } from '@dealstack/frontend/shared/components/SummaryItem/SummaryItem';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

type Props = {
	textColor?: MantineColor;
	text: string;
	noIcon?: boolean;
} & BadgeProps;
const DealMipCardBadge = ({
	textColor,
	text,
	noIcon,
	variant = 'outline',
	...restProps
}: Props) => {
	return (
		<Badge
			variant={variant}
			p="0.25rem 0.5rem"
			leftSection={noIcon ? null : <Icon variant="plant-grown" size="lg" />}
			{...restProps}
		>
			<Text size="B6Medium" c={textColor}>
				{text}
			</Text>
		</Badge>
	);
};

export const DealMipCard = styled(
	({
		className,
		onClick,
		ip,
	}: {
		onClick: () => void;
		className?: string;
		ip: Partial<SummaryDealMip>;
	}) => {
		const tAdminDashboard = useTranslationAdminDashboard();

		return (
			<Flex
				direction="column"
				justify="space-between"
				onClick={onClick}
				className={className}
			>
				<Flex direction="column" data-testid={`${TEST_ELEMENT_IDS.ADMIN_MIP_CARDS}`}>
					<SummaryItem
						item={`${TEST_ELEMENT_IDS.ADMIN_MIP_CARDS}-programmeName`}
						title={tAdminDashboard('v3.deal_modal.investment_programme')}
						value={ip.programmeName || LONG_DASH}
					/>
					<SummaryItem
						item={`${TEST_ELEMENT_IDS.ADMIN_MIP_CARDS}-entityName`}
						title={tAdminDashboard('v3.deal_modal.entity')}
						value={ip.entityName || LONG_DASH}
					/>
				</Flex>

				<div>
					<Divider m="1rem 0" color="#E9EBED" />

					<Flex justify="space-between">
						{ip.state === 'DRAFT' ? (
							<DealMipCardBadge
								c="yellow.9"
								color="yellow.8"
								textColor="yellow.8"
								text={tAdminDashboard('v3.deal_modal.draft')}
							></DealMipCardBadge>
						) : (
							<DealMipCardBadge
								c="teal.9"
								color="teal.8"
								textColor="teal.8"
								text={tAdminDashboard('v3.deal_modal.active')}
							></DealMipCardBadge>
						)}

						<DealMipCardBadge
							noIcon
							bg="gray.4"
							textColor="dark.4"
							variant="filled"
							text={`${ip.numberOfInvestors} ${tAdminDashboard('v3.deal_modal.investors')}`}
						></DealMipCardBadge>
					</Flex>
				</div>
			</Flex>
		);
	}
)`
	cursor: pointer;
	padding: 1rem;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 0.5rem;
	height: 100%;
`;
